import { useState } from "react";

import { useTranslator } from "@eo-locale/react";

import {
  FormControl,
  FormHelperText,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  FormLabel,
  VisuallyHidden,
  HStack,
  useRadio,
  Box,
  useRadioGroup,
  UseRadioProps,
  ImageEditor
} from "@/storybook-components";
import type { Question } from "../question";

interface ColourOrImageQuestionProps extends Question {
  allowImageUpload: boolean;
  onColourChangeHandler: (colourId: number) => void;
  onImageChangeHandler: (imageUrl: string) => void;
  backgroundColours: Array<{ name: string; id: number }>;
  existingIdOrUrl: { color: number; image: string };
}

const getColourForHover = (hex: string): string => {
  const colours: { [key: string]: string } = {
    "#975A16": "#744210",
    "#2C7A7B": "#285E61",
    "#B83280": "#97266D",
    "#6B46C1": "#553C9A"
  };

  if (colours[hex]) return colours[hex];

  return hex;
};

function RadioOption(props: UseRadioProps & { background: string }) {
  const { background, ...rest } = props;
  const { getInputProps, getCheckboxProps } = useRadio(rest);

  const input = getInputProps();
  const checkbox = getCheckboxProps();

  return (
    <Box as="label">
      <input {...input} title={background} />
      <Box
        {...checkbox}
        pos="relative"
        aria-hidden={true}
        cursor="pointer"
        borderRadius="full"
        background={background}
        width={{ base: 10, md: 8 }}
        height={{ base: 10, md: 8 }}
        _hover={{
          background: getColourForHover(background)
        }}
        _focus={{
          boxShadow: "outline"
        }}
        _checked={{
          "::after": {
            content: '""',
            height: "4px",
            bottom: "-1.25rem",
            position: "absolute",
            background: "brand.500",
            width: { base: 10, md: 8 }
          }
        }}
        _disabled={{
          opacity: 0.4,
          boxShadow: "none",
          cursor: "not-allowed",
          userSelect: "none",

          _hover: { background }
        }}
      />
    </Box>
  );
}

function ColourOrImageQuestion(props: ColourOrImageQuestionProps): JSX.Element {
  const {
    id,
    error,
    notes,
    status,
    question,
    required,
    allowImageUpload,
    backgroundColours,
    onColourChangeHandler,
    onImageChangeHandler,
    existingIdOrUrl
  } = props;

  const [colourId, setColourId] = useState(existingIdOrUrl?.color || "");
  const [imageUrl, setImageUrl] = useState(existingIdOrUrl?.image || "");
  const { translate } = useTranslator();

  const handleColourChange = (colourId: string): void => {
    setImageUrl("");
    setColourId(colourId);

    if (onColourChangeHandler) onColourChangeHandler(Number(colourId));
  };

  const handleImageChange = (imageUrl: string): void => {
    setColourId("");
    setImageUrl(imageUrl);

    if (onImageChangeHandler) onImageChangeHandler(imageUrl);
  };

  const { getRootProps, getRadioProps } = useRadioGroup({
    value: `${colourId}`,
    onChange: handleColourChange
  });

  const group = getRootProps();

  return (
    <FormControl
      my={4}
      id={`${id}`}
      isInvalid={error?.length > 0 || false}
      isRequired={required || false}
      isDisabled={status === "saving"}
      className={error?.length > 0 ? "ehqQuestionError" : ""}
    >
      <FormLabel pointerEvents="none">
        <HTMLWrapper html={question} />
        {required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </FormLabel>
      <FormHelperText color="gray.600">{notes}</FormHelperText>
      <HStack
        {...group}
        my={4}
        spacing={{ md: 6 }}
        justifyContent={{ sm: "space-between", md: "normal" }}
      >
        {!imageUrl &&
          backgroundColours.map(({ id, name }) => {
            const radio = getRadioProps({ value: id });
            return <RadioOption key={id} background={name} {...radio} />;
          })}
        {allowImageUpload && (
          <ImageEditor
            keepOriginalImageToBeCropped
            isDisabled={status === "saving"}
            onCropComplete={handleImageChange}
            onDelete={() => {
              onImageChangeHandler(null as any);
              setImageUrl("");
            }}
            existingImageUrl={imageUrl}
          />
        )}
      </HStack>
      {error?.length > 0 && (
        <FormErrorMessage data-testid="colourOrImageQuestion-errorMessage">
          <FormErrorIcon />
          {translate(`errors.${error}`)}
        </FormErrorMessage>
      )}
    </FormControl>
  );
}

export type { ColourOrImageQuestionProps };
export { ColourOrImageQuestion };
